import { LocatedVesselRecord } from '../../shared/contracts/vessel.types';

/**
 * Voyage Radar's VESSEL RECORD dialect — the payload shapes only this provider
 * speaks, kept in one place so nothing above has to know them.
 *
 * Two acts, and they are the two halves of "the operator answered about a
 * hull": normalise what came back into the shared record shape, and mirror it
 * into the sensor document the panels read.
 *
 * WHY A GATEWAY AND NOT THE FEATURE (rule R3). `vrToLocatedRecord` has five
 * callers spread across three features and the facade — the worldwide search
 * picks results with it, the POI list hydrates rows with it, the paid lookup
 * normalises its answer with it. If it lived on the lookup feature, the search
 * and POI features would have to import a sibling feature, which R3 forbids
 * precisely because that is how a "feature" quietly becomes a base class. As a
 * gateway function it is importable by all of them and owned by none.
 *
 * Phase 1 predicted this file BY NAME: `toLocatedRecord` was the one thing that
 * did NOT move into `located.model.ts` with the glyph rules, because it maps
 * VR's `identity`/`asOf`/`details` envelope rather than stating an AIS rule.
 * It sat on the facade for eight phases waiting for a provider-side home; this
 * is it (phase 9, REFACTOR-PLAN.md §7).
 */

/**
 * Normalise a search result OR a stored `vesselHistory` row into the one record
 * shape the map draws from.
 *
 * Both sources are accepted by the same function on purpose: a worldwide search
 * hit and the row it becomes once saved are the same vessel described twice,
 * and the map must not be able to tell them apart. The `?? source` fallbacks
 * are what make that true — a flat search hit has no `identity`/`asOf`/`details`
 * envelope, so each block reads through to the object itself.
 */
export function vrToLocatedRecord(source: any): LocatedVesselRecord | null {
  if (!source) return null;
  const identity = source.identity ?? source;
  const asOf = source.asOf ?? source;
  const details = source.details ?? source;
  const mmsi = String(source.mmsi ?? source.id ?? '').trim();
  if (!mmsi) return null;
  const num = (v: any): number | null => {
    // `Number(null)` and `Number('')` are both 0, and this runs over lat/lng:
    // a record whose fix did not survive the feed (`{ latitude: null, lat:
    // null }` reaches here as null, not undefined) would otherwise normalize
    // to 0, 0 — a real place in the Gulf of Guinea, and one that every
    // "no position for this vessel" guard downstream reads as a valid fix.
    if (v == null || (typeof v === 'string' && !v.trim())) return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };
  return {
    mmsi,
    name: identity.shipName ?? identity.name ?? null,
    shipType: identity.shipType ?? null,
    imoNumber: identity.imoNumber ?? null,
    callSign: identity.callSign ?? null,
    flag: identity.flag ?? null,
    country: identity.country ?? null,
    lat: num(asOf.latitude ?? asOf.lat),
    lng: num(asOf.longitude ?? asOf.lng),
    timeUtc: asOf.time_utc ?? null,
    navStatus: asOf.navigationalStatus ?? asOf.navStatus ?? null,
    waterBody: asOf.waterBody ?? null,
    source: source.source === 'lookup' ? 'lookup' : 'search',
    query: source.query ?? null,
    kept: false,
    // Lookup-enriched fields — absent until a "Get latest" has run.
    sog: num(asOf.sog),
    cog: num(asOf.cog),
    heading: num(asOf.heading),
    destination: details.destination ?? details.matchedDestination ?? null,
    fromPort: details.fromPort ?? null,
    length: num(details.length),
    beam: num(details.beam),
    maxDraught: num(details.maxDraught),
  };
}

/**
 * Mirror a freshly looked-up vessel into the local sensor document and hand
 * back the row that now stands for it.
 *
 * The server updated its own copy in place, so this is not a write — it is
 * keeping the client's copy honest without a refetch, which is what lets the
 * Saved Vessels panel and the map agree the instant a lookup lands.
 *
 * Merged field by field rather than replaced when the hull is already known:
 * the stored row can carry facts this response does not (`identity` survives a
 * position-only answer), and dropping them would make a "Get latest" LOSE data
 * about the vessel it just refreshed.
 *
 * The 50-record cap is the server's own `$slice`, restated here so the two
 * copies stay the same length as well as the same content — newest stay.
 */
export function vrMirrorVesselLookup(sensor: any, mmsi: string, vessel: any): any {
  const list: any[] = Array.isArray(sensor.vesselHistory) ? sensor.vesselHistory : [];
  const i = list.findIndex((v: any) => String(v?.mmsi) === mmsi);
  if (i >= 0) {
    list[i] = {
      ...list[i],
      name: vessel.name,
      identity: vessel.identity ?? list[i].identity,
      asOf: vessel.asOf ?? list[i].asOf,
      details: vessel.details ?? list[i].details,
    };
  } else {
    list.push(vessel);
    // Same cap the server enforces with $slice — newest stay.
    if (list.length > 50) list.splice(0, list.length - 50);
  }
  sensor.vesselHistory = list;
  return i >= 0 ? list[i] : list[list.length - 1];
}
